import { AppBackground, Text } from '@ui/components'
import React, { useEffect, useState } from 'react'
import { useAppSelector } from '@front/store'
import { Container, NoPetContainer, NoPermissionContainer } from './style'
import { getVetChatUserCredentialsApi } from '@front/apis'
import styled from '@emotion/native'
import { HeaderWithBackAena } from '@/components/headerwithback'
import { t } from 'i18next'

const VetContainer = styled.View(() => ({
  width: '100%',
  flex: 1,
  minHeight: 480,
}))

type Status = 'nopet' | 'loading' | 'ready' | 'error'

const VETCHAT_UNAVAILABLE = 'El chat veterinario no está disponible para esta mascota.'

// Espera (con reintentos cortos) a que el SDK de MeetingDoctors (cargado con
// `defer` en index.html) esté disponible en window antes de inicializar.
const waitForSdk = async (tries = 20): Promise<any | null> => {
  const w = window as any
  for (let i = 0; i < tries; i++) {
    if (w.meetingdoctors) return w.meetingdoctors
    await new Promise((r) => setTimeout(r, 150))
  }
  return (window as any).meetingdoctors || null
}

const VetChat: React.FC = () => {
  const petGUID = useAppSelector((state) => state.user.selectedPet)
  const [status, setStatus] = useState<Status>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!petGUID) {
      setStatus('nopet')
      return
    }
    let cancelled = false
    const initializeVetChat = async () => {
      setStatus('loading')
      setErrorMsg('')
      try {
        const response = await getVetChatUserCredentialsApi({
          petPassPetGUID: petGUID || '',
          loginProviderType: 'AENA',
        })
        if (cancelled) return
        const creds = response?.data
        if (!creds?.jwt || !creds?.meetingKey) {
          setStatus('error')
          setErrorMsg(VETCHAT_UNAVAILABLE)
          return
        }
        const sdk = await waitForSdk()
        if (cancelled) return
        if (!sdk) {
          setStatus('error')
          setErrorMsg('No se pudo cargar el chat. Recarga la página e inténtalo de nuevo.')
          return
        }
        // El contenedor ya está montado (lo renderizamos siempre), así que el SDK
        // puede inyectar el chat. defaultOpen para que el panel se vea de inmediato.
        sdk.initialize({
          apiKey: creds.meetingKey,
          jwt: creds.jwt,
          displayMode: 'contained',
          language: 'es',
          defaultOpen: true,
          containerId: 'meetingdoctors-container',
        })
        setStatus('ready')
      } catch (e: any) {
        if (cancelled) return
        console.log('ERROR INIT VETCHAT', e?.message || e)
        setStatus('error')
        setErrorMsg(e?.message || VETCHAT_UNAVAILABLE)
      }
    }
    initializeVetChat()
    return () => {
      cancelled = true
    }
  }, [petGUID])

  return (
    <AppBackground isSafe>
      <HeaderWithBackAena title={t('common.petpassvetchat')} navigateTo="/aena/home" />
      <Container>
        {status === 'nopet' && (
          <NoPetContainer>
            <Text type="H4" style={{ textAlign: 'center' }}>
              Selecciona una mascota para usar el chat veterinario.
            </Text>
          </NoPetContainer>
        )}
        {status === 'loading' && (
          <NoPetContainer>
            <Text type="H4" style={{ textAlign: 'center' }}>
              Cargando chat…
            </Text>
          </NoPetContainer>
        )}
        {status === 'error' && (
          <NoPermissionContainer>
            <Text type="H4" style={{ textAlign: 'center' }}>
              {errorMsg || VETCHAT_UNAVAILABLE}
            </Text>
          </NoPermissionContainer>
        )}
        {/* Contenedor del widget SIEMPRE montado (el SDK necesita el id en el DOM);
            se hace visible cuando el chat está listo. */}
        <VetContainer
          id="meetingdoctors-container"
          style={{ display: status === 'ready' ? 'flex' : 'none' } as any}
        />
      </Container>
    </AppBackground>
  )
}

export default VetChat
